import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, Citation, CitationType } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    const followupQuestions = answer.choices[0].context?.followup_questions;
    const messageContent = answer.choices[0].message.content;

    const citationsByName = useMemo(() => {
        const citations = answer.choices[0]?.context?.citations ?? [];
        const map = new Map<string, Citation>(citations.map(c => [c.content_name, c]));
        for (const c of citations) {
            if (c.type === CitationType.Event && c.metadata?.lorinlogs_uri) {
                map.set(c.metadata.lorinlogs_uri, c);
            }
        }
        return map;
    }, [answer]);

    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked, citationsByName), [answer]);
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml, {
        ADD_ATTR: ["target"]
    });
    const unusedChunks = useMemo(() => {
        const sharepointUris = answer.choices[0]?.context?.sharepoint_uris;
        if (!sharepointUris) return [];
        const citationSet = new Set(parsedAnswer.citations);
        return Object.keys(sharepointUris).filter(k => !citationSet.has(k));
    }, [answer, parsedAnswer.citations]);

    // Group the citations actually referenced in the answer text ([content_name]) by section.
    const sharepointCitations = parsedAnswer.citations.filter(name => citationsByName.get(name)?.type === CitationType.Sharepoint);
    const imageCitations = parsedAnswer.citations.filter(name => {
        const citation = citationsByName.get(name);
        return citation?.type === CitationType.Sharepoint && !!citation.metadata?.image_path;
    });
    const eventCitations = parsedAnswer.citations.filter(name => citationsByName.get(name)?.type === CitationType.Event);

    const navigate = useNavigate();
    // console.log(parsedAnswer);
    // console.log(answer);
    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.choices[0].context.thoughts?.length}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.choices[0].context.data_points}
                        />
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown
                        children={sanitizedAnswerHtml}
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({ node, children, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>
                            )
                        }}
                    />
                </div>
            </Stack.Item>

            {!!imageCitations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Images:</span>
                        {imageCitations.map((name, i) => {
                            const citation = citationsByName.get(name);
                            const imagePath = citation?.type === CitationType.Sharepoint ? citation.metadata?.image_path : undefined;
                            const contentUrl = imagePath ? `/content/${imagePath}` : "";
                            return (
                                <a key={i} className={styles.citation} title={name} onClick={() => onCitationClicked(contentUrl)}>
                                    {`${i + 1}. ${name}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!sharepointCitations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Sharepoint:</span>
                        {sharepointCitations.map((name, i) => {
                            const citation = citationsByName.get(name);
                            const path = citation?.type === CitationType.Sharepoint ? citation.metadata?.sharepoint_uri : undefined;
                            return (
                                <a key={i} className={styles.citation} title={name} href={path} target="_blank" rel="noopener noreferrer">
                                    {`${i + 1}. ${name}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!eventCitations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Events:</span>
                        {eventCitations.map((name, i) => {
                            const citation = citationsByName.get(name);
                            const path = citation?.type === CitationType.Event ? citation.metadata?.lorinlogs_uri : undefined;
                            const label = citation?.content_name ?? name;
                            return (
                                <a key={i} className={styles.citation} title={label} href={path} target="_blank" rel="noopener noreferrer">
                                    {`${i + 1}. ${label}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!isStreaming && !!unusedChunks.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>You might also be interested in:</span>
                        {unusedChunks.map((x, i) => {
                            const sharepointUris = answer.choices[0]?.context?.sharepoint_uris;
                            const entries = Object.entries(sharepointUris?.[x] || {});
                            const [key, value] = entries[0] || []; // Destructure the first entry
                            const path = value; // The value is the URL
                            return (
                                <a
                                    key={i}
                                    className={styles.citation}
                                    title={key}
                                    href={path} // Use href for direct URL navigation
                                    target="_blank" // Optional: If you want to open in a new tab
                                    rel="noopener noreferrer" // Optional: For security when opening external links
                                >
                                    {`${++i}. ${key}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
